import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// Typed wrapper over the resident picker's data source.
//
// Uses the DEFAULT axios instance on purpose, so the call inherits the
// interceptors from axios-setup.ts: withCredentials for the httpOnly session
// cookies, the X-CSRF-Token double-submit header, and the single-flight
// 401 -> refresh -> retry.

// EXACTLY what GET /api/residents/lookup returns, and nothing more. The backend
// deliberately withholds email, phone and document data — this is a PII
// enumeration surface — so this interface must not grow optimistic fields that
// will always be undefined.
export interface ResidentCandidate {
  occupancy_id: string;
  // NULLABLE on purpose: a spouse, a minor or a short-term resident with no
  // login is still a real resident the desk must be able to pick.
  user_id: string | null;
  display_name: string;
  // The unit is the ONLY field that distinguishes two candidates, because by
  // definition the name cannot be.
  unit_label: string;
  occupancy_status: 'current' | 'moved_out';
}

export interface ResidentLookupResponse {
  building_id: number;
  count: number;
  // How many are CURRENT. This is the number that decides whether a pick is
  // required — former residents are shown and pickable but never block a save.
  current_count: number;
  residents: ResidentCandidate[];
}

export interface ResidentLookupOptions {
  buildingId: number;
  q?: string;
  // FILTERS. Narrowing by unit is what makes most lookups unambiguous, so pass
  // it whenever the form knows the unit AND the unit is the right boundary.
  unitId?: number | null;
  /**
   * RANKS, never filters. "Search the whole building, but float this unit's
   * residents to the top."
   *
   * For the parcel handover, where the question is who COLLECTED the parcel: a
   * neighbour collecting is legitimate, and filtering to the addressed unit
   * reported them as "not a resident". Sending this does not change how many
   * candidates come back, only their order — so it cannot move the line at
   * which a pick becomes required.
   *
   * Ignored by the server when `unitId` is also sent. Send one or the other.
   */
  preferUnitId?: number | null;
  includeFormer?: boolean;
}

export const lookupResidents = async ({
  buildingId,
  q,
  unitId,
  preferUnitId,
  includeFormer = true,
}: ResidentLookupOptions): Promise<ResidentLookupResponse> => {
  const params: Record<string, string> = {
    building_id: String(buildingId),
    include_former: includeFormer ? '1' : '0',
  };
  if (q) params.q = q;
  if (unitId !== undefined && unitId !== null) params.unit_id = String(unitId);
  if (preferUnitId !== undefined && preferUnitId !== null) {
    params.prefer_unit_id = String(preferUnitId);
  }

  const res = await axios.get<ResidentLookupResponse>(`${apiRoute}/residents/lookup`, {
    params,
  });
  // Same guard as fetchActivity, for the same production Firefox finding: an
  // answer without its list must reach the caller's catch ("no candidates"),
  // not become undefined state that the name field then renders and crashes on.
  if (!res.data || !Array.isArray(res.data.residents)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Lobbero] residents lookup: an answer without residents',
      res.status,
      typeof res.data
    );
    throw new Error('residents lookup: an answer without residents');
  }

  return res.data;
};

// ---------------------------------------------------------------------------
// Capture types shared by every form that names a person.
// ---------------------------------------------------------------------------

// 'manual'      typed by a human from the start
// 'scan'        proposed by the ID scanner, untouched
// 'scan_edited' proposed by the scanner, then corrected by a human
export type ProvenanceSource = 'manual' | 'scan' | 'scan_edited';

export interface FieldProvenance {
  source: ProvenanceSource;
  // The scanner's ORIGINAL string. Sent as plaintext and encrypted server-side;
  // never send a pre-encrypted value, the backend refuses to trust one.
  raw?: string;
  confidence?: number;
}

// unset        nothing to resolve, or nobody has looked yet
// single_match the lookup returned exactly one CURRENT resident, auto-linked
// picked       2+ candidates were shown and a human chose  <- the whole point
// no_match     a human explicitly chose "none of these — not a resident"
//
// There is deliberately NO value meaning "was ambiguous, nobody chose". A CHECK
// constraint makes that combination unrepresentable in the database, so the API
// cannot construct one either.
export type ResolutionState = 'unset' | 'single_match' | 'picked' | 'no_match';

// The 400 a save returns when a name is ambiguous and nobody picked. Carries
// the candidates so the picker can reopen without making the user search again.
export interface AmbiguityError {
  message: string;
  // How many rows the picker will show, former residents included.
  candidate_count?: number;
  // How many are CURRENT — the number that made this ambiguous, and the number
  // the message quotes.
  current_count?: number;
  candidates?: ResidentCandidate[];
  resolution_field?: string;
}
